import React, { useMemo, useState } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useTranslation } from 'react-i18next';
import { ReactQueryProvider } from '@/providers/react-query-provider';
import { ToastProvider } from '@/providers/toast-provider';
import { IncomingTransactionsList } from '@/features/admin/transactions/components/incoming-transactions-list';
import {
  AdminRoutes,
  AdminSideNavigation,
} from '@/features/admin/navigation/containers/admin-side-navigation';
import { Tabs } from '@/components/design-system/molecules/tabs';
import { OutgoingTransactionsList, PayoutsList } from '@/features/admin/transactions';
import { AuthContextProvider } from '@/hooks/use-auth';
import { NotificationsProvider } from '@/features/shared/notifications/provider';
import { DiscussionThreadsProvider } from '@/features/shared/discussion-threads/providers';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { CreatePaymentModal } from '@/features/admin/transactions/components/create-payment-modal';

const TABS = [
  {
    hash: '#incoming',
    label: 'js.features.admin.transactions.transaction_page_tabs.incoming',
    Component: IncomingTransactionsList,
  },
  {
    hash: '#outgoing',
    label: 'js.features.admin.transactions.transaction_page_tabs.outgoing',
    Component: OutgoingTransactionsList,
  },
  {
    hash: '#payouts',
    label: 'js.features.admin.transactions.transaction_page_tabs.payouts',
    Component: PayoutsList,
  },
];

const TransactionsContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const selectedTab = useMemo(() => {
    const hashIndex = TABS.findIndex((tab) => tab.hash === hash);
    return hashIndex !== -1 ? hashIndex : 0;
  }, [hash]);

  return (
    <>
      <CreatePaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />
      <div className="flex h-screen w-screen">
        <AdminSideNavigation tabToSet={AdminRoutes.Transactions} />

        <div className="flex h-full flex-1 flex-col overflow-hidden px-7 pt-7">
          <div className="flex items-center justify-between pb-3">
            <h1 className="text-h3 font-bold text-text-primary">
              {t('js.features.admin.transactions.title')}
            </h1>

            {TABS[selectedTab]?.hash === '#outgoing' && (
              <button
                className="flex h-[40px] w-[161px] items-center justify-center rounded-[8px] bg-R300 px-[18px] text-body font-medium text-text-primary hover:bg-R400"
                onClick={() => setIsPaymentModalOpen(true)}
              >
                <IconSaxIcon name="add" size={20} strokeWidth={1.5} />
                {t('js.features.admin.transactions.create_payment')}
              </button>
            )}
          </div>
          <div className="flex w-full flex-1 flex-col overflow-hidden bg-E10">
            <Tabs
              selectedIndex={selectedTab}
              onChange={(index) => navigate(TABS[Number(index)].hash)}
            >
              <Tabs.TabsList>
                {TABS.map((tab) => (
                  <Tabs.Tab key={tab.hash}>
                    <Tabs.TabDefaultContent>
                      {t(tab.label) ?? ''}
                    </Tabs.TabDefaultContent>
                  </Tabs.Tab>
                ))}
              </Tabs.TabsList>

              <Tabs.TabsScreens>
                {TABS.map((tab) => (
                  <Tabs.TabScreen key={tab.hash}>
                    <tab.Component />
                  </Tabs.TabScreen>
                ))}
              </Tabs.TabsScreens>
            </Tabs>
          </div>
        </div>
      </div>
    </>
  );
};

const TransactionsScreen = () => {
  return (
    <ReactQueryProvider>
      <ToastProvider />
      <ReactQueryDevtools initialIsOpen={false} />
      <BrowserRouter>
        <AuthContextProvider>
          <DiscussionThreadsProvider>
            <NotificationsProvider>
              <TransactionsContent />
            </NotificationsProvider>
          </DiscussionThreadsProvider>
        </AuthContextProvider>
      </BrowserRouter>
    </ReactQueryProvider>
  );
};

export { TransactionsScreen };
